import * as React from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Briefcase, Building2, FileText } from 'lucide-react';
import {
  useCommandPaletteQuery,
  useRegisterCommands,
  type PaletteCommand,
} from '@/components/ui/workspace';
import { listClients } from '@/lib/api/clients';
import { DOCUMENT_PARTY_LABEL, searchEngagementDocuments } from '@/lib/api/documents';
import { listEngagements } from '@/lib/api/engagements';

/**
 * Record search for the command palette (T-0227 / C2-27).
 *
 * Mounted once in the app shell. While the palette is open and something
 * is typed, it asks the server for clients and engagements whose name
 * contains the text and, inside an engagement, documents whose title does
 * (`?q=` on the three list endpoints, which keep their caller scoping), and
 * registers the results as three groups after the navigation commands.
 * Choosing one opens the record. The server matches, so the function
 * source returns what has arrived; the query is debounced so a fast typist
 * does not fire a request per keystroke, and the previous results stay on
 * screen until the next ones land.
 */

const RESULTS_PER_GROUP = 8;
const DEBOUNCE_MS = 150;

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** The engagement whose page is open, if any (the same rule as the top bar). */
export function engagementIdFromPath(pathname: string): string | null {
  const m = pathname.match(/^\/engagements\/([0-9a-f-]{36})(?:\/|$)/i);
  return m?.[1] ?? null;
}

export function RecordSearchCommands(): null {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const engagementId = engagementIdFromPath(pathname);
  const { open, query } = useCommandPaletteQuery();
  const needle = useDebounced(query.trim(), DEBOUNCE_MS);
  const enabled = open && needle.length > 0;

  const clients = useQuery({
    queryKey: ['search', 'clients', needle] as const,
    queryFn: () => listClients({ q: needle }),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const engagements = useQuery({
    queryKey: ['search', 'engagements', needle] as const,
    queryFn: () => listEngagements({ q: needle, limit: RESULTS_PER_GROUP }),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const documents = useQuery({
    queryKey: ['search', 'documents', engagementId, needle] as const,
    queryFn: () => searchEngagementDocuments(engagementId!, needle, RESULTS_PER_GROUP),
    enabled: enabled && engagementId !== null,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const source = React.useCallback(
    (q: string): PaletteCommand[] => {
      // Nothing typed, nothing found: the navigation commands stand alone.
      if (q.trim().length === 0) return [];
      const out: PaletteCommand[] = [];
      for (const c of (clients.data ?? []).slice(0, RESULTS_PER_GROUP)) {
        out.push({
          id: `record:client:${c.id}`,
          group: 'Clients',
          label: c.name,
          ...(c.status === 'archived' ? { hint: 'Archived' } : {}),
          icon: <Building2 className="h-3.5 w-3.5" />,
          run: () => void navigate({ to: '/clients/$clientId', params: { clientId: c.id } }),
        });
      }
      for (const e of engagements.data?.engagements ?? []) {
        out.push({
          id: `record:engagement:${e.id}`,
          group: 'Engagements',
          label: e.name,
          hint: e.client_name,
          icon: <Briefcase className="h-3.5 w-3.5" />,
          run: () =>
            void navigate({ to: '/engagements/$engagementId', params: { engagementId: e.id } }),
        });
      }
      if (engagementId) {
        for (const d of documents.data?.documents ?? []) {
          out.push({
            id: `record:document:${d.id}`,
            group: 'Documents in this engagement',
            label: d.name,
            hint: d.classification ?? DOCUMENT_PARTY_LABEL[d.source],
            icon: <FileText className="h-3.5 w-3.5" />,
            run: () =>
              void navigate({
                to: '/engagements/$engagementId/documents/$documentId',
                params: { engagementId, documentId: d.id },
              }),
          });
        }
      }
      return out;
    },
    [clients.data, engagements.data, documents.data, engagementId, navigate],
  );
  useRegisterCommands(source);

  return null;
}
